import { handleActions } from 'redux-actions';
import { omit } from 'ramda';
import Immutable from 'seamless-immutable';

import { SESSION_STORAGE_KEY } from '../../lib/session-storage-keys';
import { isServer } from '../../lib/utils';

import {
  SESSION_GEO_LOCATION,
  SESSION_HYDRATE,
  SESSION_REMOVAL,
  SESSION_SET_IS_QA_TEAM,
  SESSION_SET_MEMBERSHIP_RETURN_GAME,
  SESSION_SET_PROSPECT_USER,
  SESSION_SET_SUPERCHARGER_FIRST_NAME,
  SESSION_SET_SUPERCHARGER_INVITE_TOKEN,
  SESSION_SET_SUPERCHARGER_PHONE_NUMBER,
  SESSION_SET_SUPERCHARGER_PHONE_COUNTRY,
  SESSION_SET_SUPERCHARGER_PROMO,
  SESSION_SET_USER,
  SESSION_REMOVAL_PROSPECT_USER,
  SESSION_SET_AUTH_TOKEN,
} from './actions';

/*
 * When the session is hydrated, the browser's session storage version
 * of the session is merged onto the redux version of the session (which
 * is restored on the server-side).  However, there are some parts of
 * the session that should not be overwritten. For example, the user state
 * from the server (which comes from the ping response and represents the
 * authentication state) should always override the client's.
 * logged in or not.
 */
const SERVER_PRIORITY_SESSION_KEYS = ['user', 'prospectUser'];

/**
 * @typedef {object} SessionUser
 * @property {number|string} id
 * @property {string} name
 * @property {string} displayName
 * @property {string} email
 */

/**
 * @typedef {object} SessionMembershipReturnGame
 * @property {number|string} id
 * @property {string} type
 */

/**
 * @property {SessionUser} [user]
 * @property {SessionMembershipReturnGame} [membershipReturnGame]
 */
export const INITIAL_STATE = Immutable({
  authToken: null,
  isQaTeam: false,
  loginReturn: null,
  membershipReturnGame: null,
  superchargerFirstName: null,
  superchargerInviteToken: null,
  superchargerPhoneNumber: null,
  superchargerPromo: null,
  user: null,
  prospectUser: null,
});

const saveSession = (state) => {
  !isServer &&
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  return state;
};

export const actionMap = {
  [SESSION_GEO_LOCATION]: (state, { payload }) =>
    saveSession(Immutable({ ...state, location: payload })),
  [SESSION_HYDRATE]: (state, { payload }) =>
    saveSession(
      Immutable({ ...state, ...omit(SERVER_PRIORITY_SESSION_KEYS, payload) })
    ),
  [SESSION_REMOVAL]: () => saveSession(INITIAL_STATE),
  [SESSION_SET_AUTH_TOKEN]: (state, { payload }) =>
    saveSession(Immutable({ ...state, authToken: payload })),
  [SESSION_SET_IS_QA_TEAM]: (state, { payload }) =>
    saveSession(Immutable({ ...state, isQaTeam: payload })),
  [SESSION_SET_MEMBERSHIP_RETURN_GAME]: (state, { payload }) =>
    saveSession(Immutable({ ...state, membershipReturnGame: payload })),
  [SESSION_SET_SUPERCHARGER_FIRST_NAME]: (state, { payload }) =>
    saveSession(Immutable({ ...state, superchargerFirstName: payload })),
  [SESSION_SET_SUPERCHARGER_INVITE_TOKEN]: (state, { payload }) =>
    saveSession(Immutable({ ...state, superchargerInviteToken: payload })),
  [SESSION_SET_SUPERCHARGER_PHONE_NUMBER]: (state, { payload }) =>
    saveSession(Immutable({ ...state, superchargerPhoneNumber: payload })),
  [SESSION_SET_SUPERCHARGER_PHONE_COUNTRY]: (state, { payload }) =>
    saveSession(Immutable({ ...state, superchargerPhoneCountry: payload })),
  [SESSION_SET_SUPERCHARGER_PROMO]: (state, { payload }) =>
    saveSession(Immutable({ ...state, superchargerPromo: payload })),
  [SESSION_SET_USER]: (state, { payload }) =>
    saveSession(Immutable({ ...state, user: payload })),
  [SESSION_REMOVAL_PROSPECT_USER]: (state) =>
    saveSession(Immutable({ ...state, prospectUser: null })),
  [SESSION_SET_PROSPECT_USER]: (state, { payload }) =>
    saveSession(Immutable({ ...state, prospectUser: payload })),
};

export default handleActions(actionMap, INITIAL_STATE);
